/**
 * NotificationPrefsPage — `/notifications/preferences`
 *
 * Sections:
 *  1. Master toggle — all notifications on/off
 *  2. Per-BU toggles — one row per BU in BU_SURFACES
 *  3. Quiet hours — enable + start + end time
 *  4. Email digest — weekly digest toggle with POST to API
 *
 * Persists to localStorage: "superall_notif_prefs", "superall_quiet_hours".
 * RTL support via dir attribute.
 */

import * as React from 'react';
import { useLocation } from 'wouter';
import { StaggerList, StaggerItem, FadeIn } from '@workspace/ui';

import { BU_SURFACES, type BUId } from '../lib/handoff';
import { useAuth } from '../providers/AuthProvider';
import { useLang } from '../providers/LangProvider';
import { PLATFORM_API } from '../lib/api';

// ── Storage keys ──────────────────────────────────────────────────────────────

const NOTIF_PREFS_KEY = 'superall_notif_prefs';
const QUIET_HOURS_KEY = 'superall_quiet_hours';

// ── Types ─────────────────────────────────────────────────────────────────────

interface NotifPrefs {
  all: boolean;
  bus: Partial<Record<BUId, boolean>>;
}

interface QuietHours {
  enabled: boolean;
  start: string;
  end: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function loadNotifPrefs(): NotifPrefs {
  if (typeof window === 'undefined') return { all: true, bus: {} };
  try {
    const raw = localStorage.getItem(NOTIF_PREFS_KEY);
    if (!raw) return { all: true, bus: {} };
    const parsed = JSON.parse(raw) as Partial<NotifPrefs>;
    return {
      all: parsed.all !== false,
      bus: parsed.bus ?? {},
    };
  } catch {
    return { all: true, bus: {} };
  }
}

function saveNotifPrefs(prefs: NotifPrefs) {
  try {
    localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // storage unavailable
  }
}

function loadQuietHours(): QuietHours {
  if (typeof window === 'undefined') return { enabled: false, start: '22:00', end: '08:00' };
  try {
    const raw = localStorage.getItem(QUIET_HOURS_KEY);
    if (!raw) return { enabled: false, start: '22:00', end: '08:00' };
    return JSON.parse(raw) as QuietHours;
  } catch {
    return { enabled: false, start: '22:00', end: '08:00' };
  }
}

function saveQuietHours(qh: QuietHours) {
  try {
    localStorage.setItem(QUIET_HOURS_KEY, JSON.stringify(qh));
  } catch {
    // storage unavailable
  }
}

// ── Toggle row ────────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  subLabel,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  subLabel?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  const id = React.useId();
  return (
    <div className={`flex items-center justify-between py-3 ${disabled ? 'opacity-50' : ''}`}>
      <div>
        <label
          htmlFor={id}
          className="text-sm font-medium text-neutral-900 dark:text-neutral-100 cursor-pointer"
        >
          {label}
        </label>
        {subLabel && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{subLabel}</p>
        )}
      </div>
      <button
        id={id}
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed ${
          checked ? 'bg-purple-600' : 'bg-neutral-300 dark:bg-neutral-600'
        }`}
      >
        <span
          aria-hidden="true"
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function NotificationPrefsPage(): React.ReactElement {
  const [lang] = useLang();
  const { token } = useAuth();
  const [, navigate] = useLocation();
  const isAr = lang === 'ar';

  const [prefs, setPrefs] = React.useState<NotifPrefs>(loadNotifPrefs);
  const [quietHours, setQuietHours] = React.useState<QuietHours>(loadQuietHours);
  const [emailDigest, setEmailDigest] = React.useState(false);
  const [digestMsg, setDigestMsg] = React.useState<string | null>(null);

  const title = isAr ? 'تفضيلات الإشعارات' : 'Notification preferences';

  // ── Prefs helpers ─────────────────────────────────────────────────

  function setAll(next: boolean) {
    const updated: NotifPrefs = { ...prefs, all: next };
    setPrefs(updated);
    saveNotifPrefs(updated);
  }

  function setBU(buId: BUId, next: boolean) {
    const updated: NotifPrefs = {
      ...prefs,
      bus: { ...prefs.bus, [buId]: next },
    };
    setPrefs(updated);
    saveNotifPrefs(updated);
  }

  function getBU(buId: BUId): boolean {
    return prefs.bus[buId] !== false; // default ON
  }

  // ── Quiet hours helpers ───────────────────────────────────────────

  function updateQuietHours(patch: Partial<QuietHours>) {
    const updated = { ...quietHours, ...patch };
    setQuietHours(updated);
    saveQuietHours(updated);
  }

  // ── Email digest ──────────────────────────────────────────────────

  async function toggleEmailDigest(next: boolean) {
    setEmailDigest(next);
    setDigestMsg(null);
    try {
      await fetch(`${PLATFORM_API}/preferences/email-digest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ enabled: next }),
      });
      setDigestMsg(
        next
          ? isAr
            ? 'تم تفعيل الملخص الأسبوعي'
            : 'Weekly digest enabled'
          : isAr
            ? 'تم إيقاف الملخص الأسبوعي'
            : 'Weekly digest disabled'
      );
    } catch {
      setDigestMsg(isAr ? 'تعذّر الحفظ' : 'Could not save — will retry later');
    }
  }

  return (
    <FadeIn>
      <div dir={isAr ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/notifications')}
            aria-label={isAr ? 'رجوع' : 'Back'}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
          >
            {isAr ? '→' : '←'}
          </button>
          <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{title}</h1>
        </div>

        <div className="space-y-4">
          {/* ── Master toggle ────────────────────────────────────── */}
          <section className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-2 shadow-sm">
            <ToggleRow
              label={isAr ? 'جميع الإشعارات' : 'All notifications'}
              subLabel={
                prefs.all
                  ? isAr
                    ? 'الإشعارات مُفعَّلة'
                    : 'Notifications are on'
                  : isAr
                    ? 'جميع الإشعارات متوقفة'
                    : 'All notifications are off'
              }
              checked={prefs.all}
              onChange={setAll}
            />
          </section>

          {/* ── Per-BU toggles ───────────────────────────────────── */}
          <section className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-2 shadow-sm">
            <h2 className="mb-1 pt-3 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              {isAr ? 'الخدمات' : 'Services'}
            </h2>
            <StaggerList className="divide-y divide-neutral-100 dark:divide-neutral-800" as="ul">
              {BU_SURFACES.map((bu) => (
                <StaggerItem key={bu.id} as="li">
                  <ToggleRow
                    label={`${bu.icon} ${isAr ? bu.nameAr : bu.nameEn}`}
                    checked={getBU(bu.id)}
                    disabled={!prefs.all}
                    onChange={(next) => setBU(bu.id, next)}
                  />
                </StaggerItem>
              ))}
            </StaggerList>
          </section>

          {/* ── Quiet hours ──────────────────────────────────────── */}
          <section className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-2 shadow-sm">
            <h2 className="mb-1 pt-3 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              {isAr ? 'ساعات الهدوء' : 'Quiet hours'}
            </h2>
            <ToggleRow
              label={isAr ? 'تفعيل ساعات الهدوء' : 'Enable quiet hours'}
              checked={quietHours.enabled}
              onChange={(next) => updateQuietHours({ enabled: next })}
            />
            {quietHours.enabled && (
              <div className="mb-3 flex gap-4">
                <label className="flex-1">
                  <span className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                    {isAr ? 'من' : 'From'}
                  </span>
                  <input
                    type="time"
                    value={quietHours.start}
                    onChange={(e) => updateQuietHours({ start: e.target.value })}
                    className="w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                  />
                </label>
                <label className="flex-1">
                  <span className="mb-1 block text-xs text-neutral-500 dark:text-neutral-400">
                    {isAr ? 'إلى' : 'To'}
                  </span>
                  <input
                    type="time"
                    value={quietHours.end}
                    onChange={(e) => updateQuietHours({ end: e.target.value })}
                    className="w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                  />
                </label>
              </div>
            )}
          </section>

          {/* ── Email digest ─────────────────────────────────────── */}
          <section className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-2 shadow-sm">
            <h2 className="mb-1 pt-3 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              {isAr ? 'الملخص الأسبوعي' : 'Email digest'}
            </h2>
            <ToggleRow
              label={isAr ? 'إرسال ملخص أسبوعي بالبريد' : 'Weekly email digest'}
              subLabel={
                isAr ? 'ملخص أسبوعي لنشاطك وعروضك' : 'A weekly summary of your activity and offers'
              }
              checked={emailDigest}
              onChange={(next) => void toggleEmailDigest(next)}
            />
            {digestMsg && (
              <p className="mb-3 rounded-lg bg-neutral-50 dark:bg-neutral-800 px-3 py-2 text-xs text-neutral-600 dark:text-neutral-300">
                {digestMsg}
              </p>
            )}
          </section>
        </div>
      </div>
    </FadeIn>
  );
}
